import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { deleteSupplie, fetchSupplies } from '../../features/supplies/suppliesSlice';
import { deleteLocation, fetchLocations } from '../../features/locations/locationsSlice';
import { deleteManual } from '../../features/manuals/manualsSlice';
import { deleteGroupSupplie } from '../../features/groups/groupsSlice';
import { deleteValidDocument } from '../../features/validDocuments/validDocumentsSlice';
import { deleteMaintenance } from '../../features/maintenances/maintenancesSlice';
import {
  deleteMaintenanceType,
  fetchMaintenanceTypes,
} from '../../features/maintenanceTypes/maintenanceTypesSlice';
import { ACTIONS } from '../../constants/actionTables';
import { iconButtonStyles } from '../../utils/iconButtonStyles';
import { MODAL_STYLES } from '../../utils/modalStyles';

import { IconButton, Tooltip, Typography } from '@mui/material';
import {
  ModalButton,
  ModalWrapper,
  ModalErrorDisplay,
} from '../ModalComponents';
import DeleteIcon from '@mui/icons-material/Delete';

export default function ModalDelete({
  id,
  name,
  type,
  fetchData,
  flagForDelete = true,
  buttonColor = 'primary',
}) {
  const [open, setOpen] = useState(false);
  const [errorResponse, setErrorResponse] = useState('');

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Map action types to their translation keys
  const getTypeLabel = () => {
    const typeMap = {
      [ACTIONS.SUPPLIES]: t('generic.supplies'),
      [ACTIONS.LOCATIONS]: t('generic.locations'),
      [ACTIONS.MAINTENANCETYPES]: t('generic.maintenanceType'),
      [ACTIONS.MANUAL]: t('generic.manuals'),
      [ACTIONS.SUPPLY]: t('generic.supply'),
      [ACTIONS.DOCUMENT]: t('generic.document'),
      [ACTIONS.TASK]: t('generic.task'),
    };
    return typeMap[type] || '';
  };

  const getWarningMessage = () => {
    const warningMap = {
      [ACTIONS.SUPPLIES]: t('generic.deleteSupplyWarning'),
      [ACTIONS.LOCATIONS]: t('generic.deleteLocationWarning'),
      [ACTIONS.MAINTENANCETYPES]: t('generic.deleteMaintenanceTypeWarning'),
      [ACTIONS.MANUAL]: t('generic.deleteManualWarning'),
      [ACTIONS.SUPPLY]: t('generic.deleteSupplyWarning'),
      [ACTIONS.DOCUMENT]: t('generic.deleteDocumentWarning'),
      [ACTIONS.TASK]: t('generic.deleteTaskWarning'),
    };
    return warningMap[type] || '';
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      if (type === ACTIONS.SUPPLIES) {
        await dispatch(deleteSupplie(id));
        setOpen(false);
        dispatch(fetchSupplies());
      }
      if (type === ACTIONS.LOCATIONS) {
        await dispatch(deleteLocation(id));
        setOpen(false);
        dispatch(fetchLocations());
      }
      if (type === ACTIONS.MAINTENANCETYPES) {
        const result = await dispatch(deleteMaintenanceType(id));
        if (result.meta.requestStatus === 'rejected') {
          setErrorResponse(result.payload || t('generic.deleteFailed'));
          return;
        }
        setOpen(false);
        dispatch(fetchMaintenanceTypes());
      }
      if (type === ACTIONS.MANUAL) {
        await dispatch(deleteManual(id));
        setOpen(false);
        fetchData();
      }
      if (type === ACTIONS.SUPPLY) {
        await dispatch(deleteGroupSupplie(id));
        setOpen(false);
        fetchData();
      }
      if (type === ACTIONS.DOCUMENT) {
        await dispatch(deleteValidDocument(id));
        setOpen(false);
        fetchData();
      }
      if (type === ACTIONS.TASK) {
        await dispatch(deleteMaintenance(id));
        setOpen(false);
        fetchData();
      }
    } catch (error) {
      setErrorResponse(error);
      console.error('Error delete:', error);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        {flagForDelete ? (
          <Tooltip title={t('generic.delete')} placement="top">
            <IconButton
              color={buttonColor}
              disabled={!flagForDelete}
              onClick={handleOpen}
              style={{
                ...iconButtonStyles,
                ...(buttonColor === 'error'
                  ? {
                      borderColor: '#ef4444',
                    }
                  : {}),
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t('generic.cannotDelete')} placement="top" arrow>
            <IconButton
              disabled
              style={{
                ...iconButtonStyles,
                color: 'rgba(0, 0, 0, 0.26)',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                cursor: 'not-allowed',
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <ModalWrapper
        open={open}
        handleClose={handleClose}
        title={`${t('modals.areSureWantDelete')} ${getTypeLabel()}?`}
        actions={
          <>
            <ModalButton variant="outlined" color="primary" onClick={handleClose}>
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton variant="contained" color="error" onClick={handleSubmit}>
              {t('generic.delete')}
            </ModalButton>
          </>
        }
      >
        <div style={MODAL_STYLES.formContainer}>
          <Typography variant="h6" style={{ color: 'var(--text-heading)', fontWeight: 'bold' }}>
            {name}
          </Typography>
        </div>
        <Typography color="var(--text-secondary)" variant="body2" pt={1}>
          {getWarningMessage()}
        </Typography>
        <ModalErrorDisplay errorResponse={errorResponse} />
      </ModalWrapper>
    </div>
  );
}
